import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import Table from "../../../components/Table/Table";
import { getTeams, deleteTeam } from "../../../services/olympiad/teamService";
import { getRegions } from "../../../services/olympiad/regionService";
import { getExamCenters } from "../../../services/olympiad/examCenterService";
import { toast } from "react-toastify";
import { Button } from "../../../components/forms/Buttons/Button";
import TeamPageHeader from "./PageHeader";
import TeamFilters from "./Filters";
import TeamViewModal from "./TeamViewModal";
import { useTeamColumns } from "./columns";
import { UserContext } from "../../../utils/contexts/UserContext";

export default function Teams() {
  const [t] = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [viewingTeam, setViewingTeam] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  // Filters
  const [regionFilter, setRegionFilter] = useState(null);
  const [examCenterFilter, setExamCenterFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [teamMemberSearchFilter, setTeamMemberSearchFilter] = useState("");
  const [teamNameSearchFilter, setTeamNameSearchFilter] = useState("");

  const { user } = useContext(UserContext);

  const isExamCenterAdmin = user?.roles?.some(
    (role) => role?.code === "EXAM_CENTER_ADMIN"
  );

  // Options
  const [regions, setRegions] = useState([]);
  const [examCenters, setExamCenters] = useState([]);

  useEffect(() => {
    if (isExamCenterAdmin) {
      return;
    }
    getRegions({})
      .then((res) => {
        setRegions(
          (res.results || []).map((r) => ({ label: r.name, value: r.uuid }))
        );
      })
      .catch((err) => console.error("Error fetching regions:", err));

    getExamCenters({})
      .then((res) => {
        setExamCenters(
          (res.results || []).map((ec) => ({
            label: ec.name,
            value: ec.uuid,
          }))
        );
      })
      .catch((err) => console.error("Error fetching exam centers:", err));
  }, [isExamCenterAdmin]);

  const fetchData = () => {
    setLoading(true);
    getTeams({
      region: regionFilter,
      exam_center: examCenterFilter,
      status: statusFilter,
      team_member_search: teamMemberSearchFilter || undefined,
      name: teamNameSearchFilter || undefined,
      page: page,
      page_size: 50,
    })
      .then((res) => {
        setData(res.results || []);
        setCount(res.count || 0);
        setPageCount(Math.ceil((res.count || 0) / 50));
      })
      .catch((err) => {
        console.error("Error fetching teams:", err);
        toast.error(t("admin.error_fetching_data"));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    setPage(1);
  }, [teamMemberSearchFilter, teamNameSearchFilter]);

  useEffect(() => {
    fetchData();
  }, [
    page,
    regionFilter,
    examCenterFilter,
    statusFilter,
    teamMemberSearchFilter,
    teamNameSearchFilter,
  ]);

  const handleDelete = (team) => {
    if (toast.isActive(team.uuid)) return;
    toast(
      <div className="flex flex-col gap-2 pt-2">
        <p>{t("admin.confirm_delete_team", { team: team.name })}</p>
        <div className="flex justify-end gap-2">
          <Button
            text={t("buttons.delete")}
            color="error"
            onClick={() => {
              toast.dismiss(team.uuid);
              deleteTeam(team.uuid)
                .then(() => {
                  toast.success(t("admin.team_deleted"));
                  fetchData();
                })
                .catch((err) => {
                  toast.error(
                    err.response?.data?.detail || t("admin.error_deleting_team")
                  );
                });
            }}
          />
          <Button
            text={t("buttons.cancel")}
            color="secondary"
            onClick={() => toast.dismiss(team.uuid)}
          />
        </div>
      </div>,
      { hideProgressBar: true, toastId: team.uuid }
    );
  };

  const handleView = (team) => {
    setViewingTeam(team);
    setIsViewModalOpen(true);
  };

  const columns = useTeamColumns(handleDelete, handleView, isExamCenterAdmin);

  return (
    <div className="p-6">
      <TeamPageHeader isExamCenterAdmin={isExamCenterAdmin} />

      <TeamFilters
        isExamCenterAdmin={isExamCenterAdmin}
        regionFilter={regionFilter}
        setRegionFilter={setRegionFilter}
        examCenterFilter={examCenterFilter}
        setExamCenterFilter={setExamCenterFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        teamMemberSearchFilter={teamMemberSearchFilter}
        setTeamMemberSearchFilter={setTeamMemberSearchFilter}
        teamNameSearchFilter={teamNameSearchFilter}
        setTeamNameSearchFilter={setTeamNameSearchFilter}
        regions={regions}
        examCenters={examCenters}
      />

      <Table
        columns={columns}
        data={data}
        loading={loading}
        count={count}
        pageNumber={page}
        pageCount={pageCount}
        nextPage={() => setPage((p) => Math.min(p + 1, pageCount))}
        previousPage={() => setPage((p) => Math.max(p - 1, 1))}
        gotoPage={(p) => setPage(p)}
      />

      <TeamViewModal
        isOpen={isViewModalOpen}
        team={viewingTeam}
        onClose={() => {
          setIsViewModalOpen(false);
          setViewingTeam(null);
        }}
      />
    </div>
  );
}
